import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { StripeElementsWrapper } from './StripeProvider';
import { AddCardForm } from './AddCardForm';
import { billingApi } from '../../../api/billing';
import type { SavedPaymentMethod } from '../../../lib/stripeBillingService';

interface AddCardDialogProps {
  opened: boolean;
  onClose: () => void;
  /** Called after the card is successfully saved and synced. */
  onCardSaved: () => void;
  /** Optional: receive the saved payment method and SetupIntent id (e.g. intake persistence). */
  onPaymentMethodSaved?: (pm: SavedPaymentMethod, setupIntentId: string) => void;
  /** Patient ID to scope this card to (for guarantor/proxy flows). */
  patientId?: string;
  /** Stripe 3DS return URL (defaults to client portal billing page). */
  returnUrl?: string;
}

type DialogState =
  | { phase: 'loading' }
  | { phase: 'ready'; clientSecret: string; publishableKey: string }
  | { phase: 'saving' }
  | { phase: 'error'; message: string };

/**
 * Dialog that creates a SetupIntent on open, renders Stripe PaymentElement,
 * and syncs the saved card to the backend after confirmation.
 */
export function AddCardDialog({
  opened,
  onClose,
  onCardSaved,
  onPaymentMethodSaved,
  patientId,
  returnUrl,
}: AddCardDialogProps): JSX.Element {
  const [state, setState] = useState<DialogState>({ phase: 'loading' });

  // Re-fetch when patientId changes (dialog reopened for a different subject)
  const fetchSetupIntent = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const { clientSecret, publishableKey } = await billingApi.createSetupIntent(patientId);
      if (!publishableKey) {
        setState({
          phase: 'error',
          message:
            'Stripe is not configured. Add STRIPE_PUBLISHABLE_KEY to Medplum project secrets.',
        });
        return;
      }
      setState({ phase: 'ready', clientSecret, publishableKey });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to initialize payment form.';
      setState({ phase: 'error', message });
    }
  }, [patientId]);

  useEffect(() => {
    if (opened) {
      fetchSetupIntent();
    } else {
      // Reset on close so next open gets a fresh SetupIntent
      setState({ phase: 'loading' });
    }
  }, [opened, fetchSetupIntent]);

  const handleSuccess = useCallback(
    async (setupIntentId: string, nickname: string) => {
      setState({ phase: 'saving' });
      try {
        const saved = await billingApi.syncPaymentMethod({
          setupIntentId,
          nickname: nickname || undefined,
          makeDefault: false,
          patientId,
        });

        onPaymentMethodSaved?.(saved, setupIntentId);

        notifications.show({
          color: 'green',
          title: 'Card saved',
          message: 'Your new payment method has been added successfully.',
        });

        onCardSaved();
        onClose();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Card was saved to Stripe but failed to sync. Please refresh the page.';
        setState({ phase: 'error', message });
      }
    },
    [onCardSaved, onClose, onPaymentMethodSaved, patientId],
  );

  function renderContent(): JSX.Element {
    switch (state.phase) {
      case 'loading':
        return (
          <Stack align="center" py="xl" gap="md">
            <Loader size="md" />
            <Text size="sm" c="gray.6">
              Preparing secure payment form...
            </Text>
          </Stack>
        );

      case 'saving':
        return (
          <Stack align="center" py="xl" gap="md">
            <Loader size="md" />
            <Text size="sm" c="gray.6">
              Saving your card...
            </Text>
          </Stack>
        );

      case 'error':
        return (
          <Stack gap="md">
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Something went wrong"
              color="red"
              variant="light"
            >
              {state.message}
            </Alert>
            <Group justify="flex-end">
              <Text
                size="sm"
                c="blue"
                style={{ cursor: 'pointer' }}
                onClick={fetchSetupIntent}
              >
                Try again
              </Text>
            </Group>
          </Stack>
        );

      case 'ready':
        return (
          <StripeElementsWrapper
            clientSecret={state.clientSecret}
            publishableKey={state.publishableKey}
          >
            <AddCardForm
              onSuccess={handleSuccess}
              onCancel={onClose}
              returnUrl={
                returnUrl ??
                (typeof window !== 'undefined'
                  ? `${window.location.origin}${window.location.pathname}${window.location.search}`
                  : undefined)
              }
            />
          </StripeElementsWrapper>
        );
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add payment method"
      size="md"
      closeOnClickOutside={state.phase !== 'saving'}
      closeOnEscape={state.phase !== 'saving'}
    >
      {renderContent()}
    </Modal>
  );
}
